import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createRepo, useFormatCapabilities, useProjects, useRepos, type Repo } from '../api/client';
import { FORMATS, kindsForFormat, formatCaveat, isDeprecatedFormat, isExperimentalFormat, type FormatDescriptor } from '../lib/formats';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { MemberSelector } from '../components/MemberSelector';
import { SlugInput, slugIsValid } from '../components/SlugInput';

const KINDS: Repo['kind'][] = ['local', 'remote', 'virtual'];

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function kindTone(kind: Repo['kind']): 'accent' | 'nebula' | 'neutral' {
  if (kind === 'remote') return 'nebula';
  if (kind === 'virtual') return 'accent';
  return 'neutral';
}

function formatSuffix(f: FormatDescriptor, t: (key: string) => string): string {
  if (isDeprecatedFormat(f)) return t('repos.create.deprecatedSuffix');
  if (isExperimentalFormat(f)) return t('repos.create.experimentalSuffix');
  return '';
}

const REPO_SKELETON_KEYS = Array.from({ length: 6 }, () => crypto.randomUUID());

function ReposTableBody({
  repos,
  filtered,
  isLoading,
  onRowClick,
}: Readonly<{
  repos: Repo[] | undefined;
  filtered: Repo[];
  isLoading: boolean;
  onRowClick: (id: string) => void;
}>) {
  const { t } = useTranslation();
  if (isLoading && !repos) {
    return (
      <>
        {REPO_SKELETON_KEYS.map((key) => (
          <tr key={key} className="border-b border-border/60 last:border-0">
            <td className="px-5 py-3"><Skeleton width="7rem" /></td>
            <td className="px-5 py-3"><Skeleton width="10rem" /></td>
            <td className="px-5 py-3"><Skeleton width="4rem" height="1.25rem" rounded="9999px" /></td>
            <td className="px-5 py-3"><Skeleton width="5rem" /></td>
            <td className="px-5 py-3"><Skeleton width="60%" /></td>
          </tr>
        ))}
      </>
    );
  }

  if (filtered.length) {
    return (
      <>
        {filtered.map((r) => (
          <tr
            key={r.id}
            className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40 cursor-pointer"
            onClick={() => onRowClick(r.id)}
          >
            <td className="px-5 py-3 font-mono text-accent">{r.slug}</td>
            <td className="px-5 py-3 text-fg">{r.name}</td>
            <td className="px-5 py-3">
              <Badge tone={kindTone(r.kind)}>{r.kind}</Badge>
              {r.capabilityWarning && (
                <span data-testid="capability-warning" title={r.capabilityWarning} className="ml-2">
                  <Badge tone="warning">{t('repos.table.unsupportedKind')}</Badge>
                </span>
              )}
            </td>
            <td className="px-5 py-3"><span className="orb-code">{r.format}</span></td>
            <td className="px-5 py-3 text-fg-muted break-all">{r.upstreamUrl}</td>
          </tr>
        ))}
      </>
    );
  }

  return (
    <tr>
      <td colSpan={5} className="px-5 py-8 text-center text-fg-muted text-sm">
        {repos && repos.length > 0 ? t('repos.table.noneFiltered') : t('repos.table.empty')}
      </td>
    </tr>
  );
}

export function Repos() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: repos, mutate, isLoading } = useRepos();
  const { data: projects } = useProjects();
  const { data: capData } = useFormatCapabilities();

  // v189 — pre-select format from `?format=<id>` query param so the
  // dashboard's format-tile click lands here with the right adapter
  // already chosen. `?new=1` additionally scrolls the create-form
  // into view.
  const initialFormat = (() => {
    const fromQuery = searchParams.get('format');
    if (fromQuery && FORMATS.some((f) => f.id === fromQuery)) {
      return fromQuery as Repo['format'];
    }
    return 'maven' as Repo['format'];
  })();

  const [form, setForm] = useState<Omit<Repo, 'id'>>({
    projectId: '', slug: '', name: '', kind: 'local', format: initialFormat, upstreamUrl: '', members: [],
  });
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const selectedFormat = FORMATS.find((f) => f.id === form.format);
  const createKinds = kindsForFormat(KINDS, form.format, capData?.formats);

  // Client-side filters over the fetched repo list.
  const [fKind, setFKind] = useState<'all' | Repo['kind']>('all');
  const [fFormat, setFFormat] = useState<'all' | Repo['format']>('all');
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const filtered = (repos ?? []).filter(
    (r) =>
      (fKind === 'all' || r.kind === fKind) &&
      (fFormat === 'all' || r.format === fFormat) &&
      (needle === '' || r.slug.toLowerCase().includes(needle) || r.name.toLowerCase().includes(needle)),
  );
  const tableTitle =
    repos && filtered.length !== repos.length
      ? t('repos.table.titleFiltered', { count: filtered.length, total: repos.length })
      : t('repos.table.title', { count: filtered.length });
  const formRef = useRef<HTMLFormElement | null>(null);

  // If the user arrived via "/repos?format=...&new=1" (dashboard click),
  // scroll the create-form into view + clear the query so a refresh
  // doesn't re-trigger the scroll.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      next.delete('format');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setCreateError(null);
    try {
      // Strip members for non-virtual kinds so the backend doesn't have
      // to reject the body for non-virtual + non-empty members.
      const body: Omit<Repo, 'id'> =
        form.kind === 'virtual'
          ? form
          : { ...form, members: undefined };
      await createRepo(body);
      setForm({ ...form, slug: '', name: '', upstreamUrl: '', members: [] });
      await mutate();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">{t('repos.eyebrow')}</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">{t('repos.title')}</h1>
        <p className="text-fg-muted text-sm mt-2">
          {t('repos.intro')}{' '}
          <span className="ml-1"><Badge tone="neutral">local</Badge></span>
          <span className="ml-1"><Badge tone="nebula">remote</Badge></span>
          <span className="ml-1"><Badge tone="accent">virtual</Badge></span>
        </p>
      </div>

      <Card>
        <CardHeader title={t('repos.create.title')} />
        <CardBody>
          <form ref={formRef} onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.target.value })}
              required
              aria-label={t('repos.create.projectLabel')}
              className={selectClass}
            >
              <option value="">{t('repos.create.projectPlaceholder')}</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <SlugInput
              value={form.slug}
              onChange={(slug) => setForm({ ...form, slug })}
              required
            />
            <Input
              placeholder={t('repos.create.namePlaceholder')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as Repo['kind'] })}
              aria-label={t('repos.create.kindLabel')}
              className={selectClass}
            >
              {createKinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <div className="md:col-span-1">
              <select
                value={form.format}
                onChange={(e) => {
                  const format = e.target.value as Repo['format'];
                  const ok = kindsForFormat(KINDS, format, capData?.formats);
                  setForm({ ...form, format, kind: ok.includes(form.kind) ? form.kind : 'local', members: ok.includes(form.kind) ? form.members : [] });
                }}
                aria-label={t('repos.create.formatLabel')}
                className={selectClass}
              >
                {FORMATS.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                    {formatSuffix(f, t)}
                  </option>
                ))}
              </select>
              {selectedFormat && isDeprecatedFormat(selectedFormat) && (
                <p className="mt-1">
                  <Badge tone="warning">{formatCaveat(selectedFormat) ?? t('repos.create.deprecatedFallback')}</Badge>
                </p>
              )}
            </div>
            <Input
              placeholder={t('repos.create.upstreamPlaceholder')}
              value={form.upstreamUrl}
              onChange={(e) => setForm({ ...form, upstreamUrl: e.target.value })}
            />
            {form.kind === 'virtual' && (
              <div className="md:col-span-3 space-y-2">
                <p className="orb-section-title text-fg-muted">
                  {t('repos.create.membersTitle')}
                </p>
                <p className="text-xs text-fg-muted">
                  {t('repos.create.membersHint')}
                </p>
                <MemberSelector
                  projectId={form.projectId}
                  format={form.format}
                  value={form.members ?? []}
                  onChange={(members) => setForm({ ...form, members })}
                />
              </div>
            )}
            <Button
              variant="primary"
              type="submit"
              disabled={busy || !slugIsValid(form.slug)}
              className="md:col-span-3"
            >
              <Plus className="h-4 w-4" />
              {t('repos.create.submit')}
            </Button>
            {createError && (
              <p role="alert" className="md:col-span-3 text-sm text-danger">{createError}</p>
            )}
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={tableTitle}
        />
        <div className="px-5 pb-3 flex flex-wrap items-center gap-2">
          <select value={fKind} onChange={(e) => setFKind(e.target.value as 'all' | Repo['kind'])} className={selectClass} aria-label={t('repos.filters.kindLabel')}>
            <option value="all">{t('repos.filters.allKinds')}</option>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select value={fFormat} onChange={(e) => setFFormat(e.target.value as 'all' | Repo['format'])} className={selectClass} aria-label={t('repos.filters.formatLabel')}>
            <option value="all">{t('repos.filters.allFormats')}</option>
            {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('repos.filters.searchPlaceholder')}
            className={`${selectClass} flex-1 min-w-[12rem]`}
          />
          {(fKind !== 'all' || fFormat !== 'all' || q !== '') && (
            <button
              type="button"
              onClick={() => { setFKind('all'); setFFormat('all'); setQ(''); }}
              className="h-10 px-3 rounded-md border border-border text-fg-muted hover:text-fg hover:bg-bg-hover/40 text-sm"
            >
              {t('repos.filters.clear')}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">{t('repos.table.slug')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('repos.table.name')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('repos.table.kind')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('repos.table.format')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('repos.table.upstream')}</th>
              </tr>
            </thead>
            <tbody>
              <ReposTableBody
                repos={repos}
                filtered={filtered}
                isLoading={isLoading}
                onRowClick={(id) => navigate(`/repos/${id}`)}
              />
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
